import { type RefObject, useLayoutEffect } from 'react';

export const PODCAST_EPISODE_AUDIO_PLAYER_SELECTOR = '.podcast-episode-paywall-anchor';
const PAYWALL_GAP_PX = 16;
const PAYWALL_GRADIENT_HEIGHT_PX = 120;
const FOOTER_GAP_PX = 32;
const BLUR_CONTENT_SELECTOR = '.rich-text-element';

const resetMinHeights = (root: HTMLElement) => {
  const previousRootMinHeight = root.style.minHeight;
  root.style.minHeight = 'auto';

  const sections = root.querySelectorAll('section');
  const previousSectionMinHeights = Array.from(sections).map((section) => {
    if (!(section instanceof HTMLElement)) {
      return '';
    }

    const previousMinHeight = section.style.minHeight;
    section.style.minHeight = 'auto';
    return previousMinHeight;
  });

  return () => {
    root.style.minHeight = previousRootMinHeight;
    sections.forEach((section, index) => {
      if (section instanceof HTMLElement) {
        section.style.minHeight = previousSectionMinHeights[index] ?? '';
      }
    });
  };
};

// No filter transition: every re-measure clears and re-applies the blur, so a fade-in would flash the content sharp.
const applyBlur = (targets: HTMLElement[]) => {
  targets.forEach((element) => {
    element.style.filter = 'blur(4px)';
    element.style.userSelect = 'none';
    element.style.pointerEvents = 'none';
  });

  return () => {
    targets.forEach((element) => {
      element.style.filter = '';
      element.style.userSelect = '';
      element.style.pointerEvents = '';
    });
  };
};

const getContentContainer = (audioPlayer: HTMLElement, root: HTMLElement) => {
  let current: HTMLElement | null = audioPlayer;

  while (current?.parentElement && current.parentElement !== root) {
    if (current.nextElementSibling) {
      return current;
    }

    current = current.parentElement;
  }

  return audioPlayer;
};

const getContentBelowAudioPlayer = (audioPlayer: HTMLElement, root: HTMLElement) => {
  const playerRect = audioPlayer.getBoundingClientRect();
  const targets = new Set<HTMLElement>();

  root.querySelectorAll<HTMLElement>(BLUR_CONTENT_SELECTOR).forEach((element) => {
    if (element.getBoundingClientRect().top >= playerRect.bottom - 1) {
      targets.add(element);
    }
  });

  const playerContainer = getContentContainer(audioPlayer, root);
  let sibling = playerContainer.nextElementSibling;

  while (sibling) {
    if (sibling instanceof HTMLElement) {
      targets.add(sibling);
    }

    sibling = sibling.nextElementSibling;
  }

  return Array.from(targets);
};

const usePodcastEpisodePaywallEffects = (containerRef: RefObject<HTMLDivElement | null>, onReady: () => void) => {
  useLayoutEffect(() => {
    const paywall = containerRef.current;
    const wrapper = paywall?.parentElement;
    const root = paywall?.previousElementSibling;

    if (!(paywall instanceof HTMLElement) || !(wrapper instanceof HTMLElement) || !(root instanceof HTMLElement)) {
      onReady();
      return;
    }

    const audioPlayer = root.querySelector(PODCAST_EPISODE_AUDIO_PLAYER_SELECTOR);

    if (!(audioPlayer instanceof HTMLElement)) {
      onReady();
      return;
    }

    const previousPaddingBottom = wrapper.style.paddingBottom;
    let resetBlurCleanup = () => {};

    const updateLayout = () => {
      resetBlurCleanup();

      const wrapperRect = wrapper.getBoundingClientRect();
      const playerRect = audioPlayer.getBoundingClientRect();
      const paywallTop = Math.max(0, playerRect.bottom - wrapperRect.top + PAYWALL_GAP_PX + PAYWALL_GRADIENT_HEIGHT_PX);
      paywall.style.top = `${paywallTop}px`;
      paywall.style.bottom = '0';

      const paywallContent = paywall.lastElementChild;
      const paywallContentHeight =
        paywallContent instanceof HTMLElement ? paywallContent.getBoundingClientRect().height : 0;
      const rootBottom = root.getBoundingClientRect().bottom - wrapperRect.top;
      const reservedFooterSpace = Math.max(0, paywallTop + paywallContentHeight + FOOTER_GAP_PX - rootBottom);
      wrapper.style.paddingBottom = `${reservedFooterSpace}px`;

      resetBlurCleanup = applyBlur(getContentBelowAudioPlayer(audioPlayer, root));
    };

    const resetMinHeightsCleanup = resetMinHeights(root);
    updateLayout();
    onReady();

    const observer = new ResizeObserver(updateLayout);
    observer.observe(wrapper);
    observer.observe(root);
    observer.observe(audioPlayer);
    // A block that loads late next to the player moves it without resizing root, so watch the columns that hold it.
    for (let column = audioPlayer.parentElement; column && column !== root; column = column.parentElement) {
      observer.observe(column);
    }

    return () => {
      observer.disconnect();
      resetMinHeightsCleanup();
      resetBlurCleanup();
      wrapper.style.paddingBottom = previousPaddingBottom;
      paywall.style.top = '';
      paywall.style.bottom = '';
    };
  }, [containerRef, onReady]);
};

export default usePodcastEpisodePaywallEffects;
